/**
 * Environment configuration for UI service
 * Supports 4 environments: local | development | staging | production
 *
 * Set REACT_APP_ENV to one of these values (or leave unset for 'local' when NODE_ENV=development).
 * Individual REACT_APP_*_URL vars override the env defaults when set.
 */

export const ENV_NAMES = {
  LOCAL: "local",
  DEVELOPMENT: "development",
  STAGING: "staging",
  PRODUCTION: "production",
};

/**
 * Default base URLs per environment (no trailing slash).
 * Replace placeholders with your actual hostnames.
 */
export const ENV_CONFIG = {
  [ENV_NAMES.LOCAL]: {
    backend: "http://localhost:3000",
    user: "http://localhost:3001",
    ai: "http://localhost:5001",
    integration: "http://localhost:5002",
  },
  [ENV_NAMES.DEVELOPMENT]: {
    backend: "https://dev-api.yourdomain.com",
    user: "https://dev-user.yourdomain.com",
    ai: "https://dev-ai.yourdomain.com",
    integration: "https://dev-integration.yourdomain.com",
  },
  [ENV_NAMES.STAGING]: {
    backend: "https://staging-api.yourdomain.com",
    user: "https://staging-user.yourdomain.com",
    ai: "https://staging-ai.yourdomain.com",
    integration: "https://staging-integration.yourdomain.com",
  },
  [ENV_NAMES.PRODUCTION]: {
    backend: "https://api.yourdomain.com",
    user: "https://user.yourdomain.com",
    ai: "https://ai.yourdomain.com",
    integration: "https://integration.yourdomain.com",
  },
};

const ENV_VAR_MAP = {
  backend: "REACT_APP_API_URL",
  user: "REACT_APP_USER_SERVICE_URL",
  ai: "REACT_APP_AI_SERVICE_URL",
  integration: "REACT_APP_INTEGRATION_SERVICE_URL",
};

/**
 * Resolve current environment name.
 * Order: REACT_APP_ENV → NODE_ENV (production → production, else local for dev)
 */
export const getCurrentEnv = () => {
  const env = process.env.REACT_APP_ENV?.toLowerCase();
  if (env && Object.values(ENV_NAMES).includes(env)) return env;
  if (process.env.NODE_ENV === "production") return ENV_NAMES.PRODUCTION;
  return ENV_NAMES.LOCAL;
};

/**
 * Get base URL for a service.
 * Uses explicit REACT_APP_* URL if set, otherwise env default for current REACT_APP_ENV.
 */
export const getServiceBaseURL = (service) => {
  const envVar = ENV_VAR_MAP[service];
  const explicit = process.env[envVar];
  if (explicit && typeof explicit === "string") {
    return explicit.replace(/\/$/, "");
  }
  const currentEnv = getCurrentEnv();
  const base = ENV_CONFIG[currentEnv]?.[service];
  if (base) return base.replace(/\/$/, "");
  const defaults = { backend: 3000, user: 3001, ai: 5001, integration: 5002 };
  return `http://localhost:${defaults[service] ?? 3000}`;
};

/**
 * All base URLs for the current environment (with overrides applied).
 */
export const getBaseURLs = () => ({
  backend: getServiceBaseURL("backend"),
  user: getServiceBaseURL("user"),
  ai: getServiceBaseURL("ai"),
  integration: getServiceBaseURL("integration"),
});

const envConfig = {
  ENV_NAMES,
  ENV_CONFIG,
  getCurrentEnv,
  getServiceBaseURL,
  getBaseURLs,
};

export default envConfig;
